<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { timesheetsApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import {
    Clock, Loader, ChevronDown, ChevronRight, Receipt,
    CheckCircle, AlertCircle
  } from 'lucide-svelte';

  let summaryData = null;
  let totals = null;
  let loading = true;
  let error = '';
  let selectedYear = new Date().getFullYear();
  let expandedMonths = {};

  const availableYears = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 3; y--) {
    availableYears.push(y);
  }

  onMount(() => {
    loadSummary();
  });

  async function loadSummary() {
    loading = true;
    error = '';
    try {
      console.log(`[CustomerTimesheets] Loading summary for year=${selectedYear}`);
      const response = await timesheetsApi.getCustomerSummary({ year: selectedYear });
      summaryData = response.summary || [];
      totals = response.totals || {};
      // Expand current month by default
      const currentMonth = new Date().toISOString().slice(0, 7);
      if (summaryData.some(m => m.month === currentMonth)) {
        expandedMonths[currentMonth] = true;
      } else if (summaryData.length > 0) {
        expandedMonths[summaryData[0].month] = true;
      }
      console.log(`[CustomerTimesheets] Loaded ${summaryData.length} months`);
    } catch (err) {
      console.error('[CustomerTimesheets] Error loading summary:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  function toggleMonth(month) {
    expandedMonths[month] = !expandedMonths[month];
    expandedMonths = { ...expandedMonths };
  }

  function formatMonthLabel(monthStr) {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  }

  function formatHours(hours) {
    return (hours || 0).toFixed(2);
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  function handleYearChange() {
    expandedMonths = {};
    loadSummary();
  }
</script>

<Layout>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <div class="p-3 bg-linear-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg">
          <Clock size={28} class="text-white" />
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('customerTimesheets.title')}</h1>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customerTimesheets.subtitle')}</p>
        </div>
      </div>

      <div>
        <select
          bind:value={selectedYear}
          onchange={handleYearChange}
          class="input"
        >
          {#each availableYears as year}
            <option value={year}>{year}</option>
          {/each}
        </select>
      </div>
    </div>

    <!-- Year Totals -->
    {#if totals && !loading}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="card p-4">
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customerTimesheets.totalHours')}</p>
          <p class="text-2xl font-bold text-gray-900 dark:text-white">{formatHours(totals.totalHours)}h</p>
        </div>
        <div class="card p-4">
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customerTimesheets.totalAmount')}</p>
          <p class="text-2xl font-bold text-primary-600 dark:text-primary-400">{formatCurrency(totals.totalAmount)}</p>
        </div>
        <div class="card p-4">
          <div class="flex items-center space-x-1">
            <CheckCircle size={14} class="text-emerald-500" />
            <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customerTimesheets.billedAmount')}</p>
          </div>
          <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(totals.billedAmount)}</p>
          <p class="text-xs text-gray-400">{formatHours(totals.billedHours)}h</p>
        </div>
        <div class="card p-4">
          <div class="flex items-center space-x-1">
            <AlertCircle size={14} class="text-orange-500" />
            <p class="text-sm text-gray-500 dark:text-gray-400">{$_('customerTimesheets.unbilledAmount')}</p>
          </div>
          <p class="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(totals.unbilledAmount)}</p>
          <p class="text-xs text-gray-400">{formatHours(totals.unbilledHours)}h</p>
        </div>
      </div>
    {/if}

    <!-- Monthly Breakdown -->
    <div class="space-y-3">
      {#if loading}
        <div class="card p-12 text-center">
          <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
          <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
        </div>
      {:else if error}
        <div class="card p-12 text-center">
          <p class="text-red-500">{error}</p>
        </div>
      {:else if !summaryData || summaryData.length === 0}
        <div class="card p-12 text-center">
          <Clock size={48} class="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p class="text-gray-500 dark:text-gray-400 text-lg">{$_('customerTimesheets.noData')}</p>
        </div>
      {:else}
        {#each summaryData as monthData (monthData.month)}
          <div class="card overflow-hidden">
            <!-- Month Header (clickable) -->
            <button
              class="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              onclick={() => toggleMonth(monthData.month)}
            >
              <div class="flex items-center space-x-3">
                {#if expandedMonths[monthData.month]}
                  <ChevronDown size={20} class="text-primary-500" />
                {:else}
                  <ChevronRight size={20} class="text-gray-400" />
                {/if}
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {formatMonthLabel(monthData.month)}
                </h3>
              </div>
              <div class="flex items-center space-x-6 text-sm">
                <span class="text-gray-500 dark:text-gray-400">
                  {formatHours(monthData.totalHours)}h
                </span>
                <span class="font-medium text-primary-600 dark:text-primary-400">
                  {formatCurrency(monthData.totalAmount)}
                </span>
                <div class="flex items-center space-x-3">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle size={10} class="mr-1" />
                    {formatCurrency(monthData.billedAmount)}
                  </span>
                  {#if monthData.unbilledAmount > 0}
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                      <AlertCircle size={10} class="mr-1" />
                      {formatCurrency(monthData.unbilledAmount)}
                    </span>
                  {/if}
                </div>
              </div>
            </button>

            <!-- Activity Type Breakdown -->
            {#if expandedMonths[monthData.month]}
              <div class="border-t border-gray-200 dark:border-gray-700">
                <table class="w-full">
                  <thead class="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('customerTimesheets.activityType')}</th>
                      <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('customerTimesheets.hours')}</th>
                      <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('customerTimesheets.amount')}</th>
                      <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('customerTimesheets.billedLabel')}</th>
                      <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('customerTimesheets.unbilledLabel')}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                    {#each monthData.activities as activity}
                      <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td class="px-6 py-3">
                          <span class="font-medium text-gray-900 dark:text-white">
                            {$_(`timesheets.activityTypes.${activity.activityType}`)}
                          </span>
                        </td>
                        <td class="px-6 py-3 text-right text-gray-600 dark:text-gray-300">
                          {formatHours(activity.totalHours)}h
                        </td>
                        <td class="px-6 py-3 text-right font-medium text-primary-600 dark:text-primary-400">
                          {formatCurrency(activity.totalAmount)}
                        </td>
                        <td class="px-6 py-3 text-right">
                          <span class="text-emerald-600 dark:text-emerald-400">
                            {formatHours(activity.billedHours)}h ({formatCurrency(activity.billedAmount)})
                          </span>
                        </td>
                        <td class="px-6 py-3 text-right">
                          {#if activity.unbilledHours > 0}
                            <span class="text-orange-600 dark:text-orange-400">
                              {formatHours(activity.unbilledHours)}h ({formatCurrency(activity.unbilledAmount)})
                            </span>
                          {:else}
                            <span class="text-gray-400">-</span>
                          {/if}
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                  <tfoot class="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <td class="px-6 py-3 font-semibold text-gray-900 dark:text-white">{$_('customerTimesheets.monthTotal')}</td>
                      <td class="px-6 py-3 text-right font-semibold text-gray-900 dark:text-white">{formatHours(monthData.totalHours)}h</td>
                      <td class="px-6 py-3 text-right font-semibold text-primary-600 dark:text-primary-400">{formatCurrency(monthData.totalAmount)}</td>
                      <td class="px-6 py-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatHours(monthData.billedHours)}h ({formatCurrency(monthData.billedAmount)})
                      </td>
                      <td class="px-6 py-3 text-right font-semibold text-orange-600 dark:text-orange-400">
                        {#if monthData.unbilledHours > 0}
                          {formatHours(monthData.unbilledHours)}h ({formatCurrency(monthData.unbilledAmount)})
                        {:else}
                          <span class="text-gray-400">-</span>
                        {/if}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
</Layout>
